import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardFooter,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Layout,
  CheckCircle,
  Loader2,
  ArrowRight,
  XCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import SituationStatus from "./SituationStatus";

const SituationsPage = () => {
  const supabase = useSupabase();
  const navigate = useNavigate();
  const [situations, setSituations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      navigate("/login");
    } else {
      setUser(storedUser);
      fetchSituations(storedUser.id);
    }
  }, [navigate]);

  const fetchSituations = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      // Get all user facilities
      const { data: userFacilities, error: ufError } = await supabase
        .from("user_facilities")
        .select("facility_id")
        .eq("user_id", userId)
        .eq("is_active", true);

      if (ufError) throw ufError;

      if (!userFacilities?.length) {
        setSituations([]);
        setLoading(false);
        return;
      }

      // Get all situations the user has access to through their facilities
      const { data: accessData, error: accessError } = await supabase
        .from("access_control")
        .select(
          `
          situation_id,
          facility_id,
          situations (
            id,
            title,
            description
          )
        `
        )
        .in(
          "facility_id",
          userFacilities.map((uf) => uf.facility_id)
        )
        .eq("user_id", userId);

      if (accessError) throw accessError;

      console.log("User ID being used for query:", userId);
      console.log("Access data returned:", accessData);

      // Get all user solutions
      const { data: solutionsData, error: solutionsError } = await supabase
        .from("solutions")
        .select("situation_id, user_id")
        .eq("user_id", userId);

      if (solutionsError) throw solutionsError;

      // For each situation, check if all employees have submitted their solutions
      const situationsWithStatus = await Promise.all(
        accessData.map(async (access) => {
          // Get all users who have access to this situation in the same facility
          const { data: allUsers, error: usersError } = await supabase
            .from("access_control")
            .select("user_id")
            .eq("situation_id", access.situation_id)
            .eq("facility_id", access.facility_id);

          if (usersError) throw usersError;

          // Get all solutions for this situation from users in the same facility
          const { data: allSolutions, error: solError } = await supabase
            .from("solutions")
            .select("id, user_id")
            .eq("situation_id", access.situation_id)
            .in(
              "user_id",
              allUsers.map((u) => u.user_id)
            );

          if (solError) throw solError;

          // Get all evaluations made by current user for this situation
          const { data: userEvaluations, error: evalError } = await supabase
            .from("evaluations")
            .select("solution_id")
            .eq("evaluator_id", userId)
            .in(
              "solution_id",
              allSolutions.map((s) => s.id)
            );

          if (evalError) throw evalError;

          const uniqueRespondents = new Set(allSolutions.map((s) => s.user_id));
          const allRespondedCount = uniqueRespondents.size;
          const totalUsersCount = allUsers.length;
          const allResponded = allRespondedCount === totalUsersCount;

          // Check if user has evaluated all solutions except their own
          const otherSolutions = allSolutions.filter(
            (s) => s.user_id !== userId
          );
          const allSolutionIds = new Set(otherSolutions.map((s) => s.id));
          const evaluatedSolutionIds = new Set(
            userEvaluations.map((e) => e.solution_id)
          );

          // Only consider fully evaluated when all other solutions have been evaluated
          const userOwnSolution = allSolutions.find(
            (s) => s.user_id === userId
          );

          // Consider fully evaluated only when:
          // 1. User has evaluated all other solutions AND
          // 2. User has evaluated their own solution
          const isEvaluated =
            otherSolutions.length > 0 &&
            otherSolutions.every((solution) =>
              evaluatedSolutionIds.has(solution.id)
            ) &&
            (userOwnSolution
              ? evaluatedSolutionIds.has(userOwnSolution.id)
              : true);

          return {
            id: access.situations.id,
            title: access.situations.title,
            description: access.situations.description,
            isCompleted: solutionsData.some(
              (s) => s.situation_id === access.situation_id
            ),
            allResponded,
            isEvaluated,
            respondedCount: allRespondedCount,
            totalCount: totalUsersCount,
          };
        })
      );

      setSituations(situationsWithStatus);
    } catch (error) {
      console.error("Error fetching situations:", error);
      setError("Failed to load situations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-4xl mx-auto p-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-8">
      <Card className="mb-8 shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto bg-red-100 text-red-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
            <Layout className="w-6 h-6" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            Vitajte v programe Soluteria!
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 mt-2">
            Vyberte situáciu, s ktorou chcete pracovať.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {situations.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-6 text-center text-gray-500 flex flex-col items-center">
              <XCircle className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-semibold">Žiadne situácie</p>
              <p>Momentálne nemáte prístup k žiadnym situáciám.</p>
            </CardContent>
          </Card>
        ) : (
          situations.map((situation) => (
            <SituationStatus
              key={situation.id}
              situation={{
                ...situation,
                onAction: (sit) => {
                  if (!sit.isCompleted) {
                    navigate(`/situation/${sit.id}`);
                  } else if (sit.allResponded) {
                    navigate(`/evaluate-solutions/${sit.id}`);
                  }
                },
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default SituationsPage;
